<template>
  <div>
    <PageAllow controller="FoodFormula" action="index" />
    <Breadcrumbs :items="items" />

    <v-sheet v-show="!currentItemMode" class="transparent-background-color">
      <v-row dense class="mx-5">
        <v-col cols="12" sm="12" md="4">
          <v-row dense align="center" class="pa-1">
            <SearcherKeyword
              ref="order_code"
              v-model="options.formula_keyword"
              @text="formulaKeyword = $event"
              :label="$t('Recipes.recipesKeyword')"
            />
          </v-row>
        </v-col>
        <v-col cols="12" sm="12" md="8" align-self="center">
          <v-row dense align="center">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="px-0 py-0 caption font-weight-bold grey--text"
                  color="primary"
                  @click.stop="onSearch()"
                >
                  <v-icon small class="pe-1">fa-search</v-icon>
                  {{ $t("Search.search") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.searchTip") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="caption font-weight-bold grey--text"
                  @click.stop="resetSearch()"
                >
                  <v-icon x-small class="pe-1">fa-trash-alt</v-icon>
                  {{ $t("Search.reset") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.resetTip") }}</span>
            </v-tooltip>
            <v-spacer />
            <v-btn
              small
              color="primary"
              @click.stop="
                currentItem = { id: 0 };
                editDialog = true;
              "
              class="ms-1"
            >
              <v-icon x-small class="pe-1">fa-plus</v-icon>
              {{ $t("CRUD.newInfo", [$t("Recipes.costCard")]) }}
            </v-btn>
            <v-btn
              v-if="$permission.permissionAllow('Foods', 'importBom')"
              small
              dark
              color="print"
              @click.stop="ImportDialog = true"
              class="ms-1"
            >
              <v-icon x-small class="pe-1">fa-file-import</v-icon>
              {{ $t("Import.import", [$t("Recipes.foodBom")]) }}
            </v-btn>
            <v-btn
              v-if="
                $permission.permissionAllow('FoodFormula', 'importBatchEditBom')
              "
              small
              dark
              color="print"
              @click.stop="ImportBatchEditDialog = true"
              class="ms-1"
            >
              <v-icon x-small class="pe-1">fa-file-import</v-icon>
              {{ $t("Recipes.batchEditBomImport") }}
            </v-btn>
          </v-row>
        </v-col>
      </v-row>

      <div class="mx-4 mb-4">
        <v-data-table
          :headers="headers"
          :items="recipes"
          :options.sync="options"
          :page.sync="paging.page"
          :server-items-length="paging.count"
          :loading="loading"
          multi-sort
          class="elevation-2 px-4 pt-1"
          :footer-props="$t('$config.footer-props')"
        >
          <template v-slot:header.actions="{ header }">
            {{ header.text }}
            <v-icon x-small>fa-sliders-h</v-icon>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-tooltip
              top
              v-if="$permission.permissionAllow('FoodFormula', 'edit')"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  color="primary"
                  class="px-1 py-4 me-1"
                  @click.stop="
                    currentItem = item;
                    editDialog = true;
                  "
                  >fa-pencil-alt
                </v-icon>
              </template>
              <span>{{ $t("CRUD.editInfo", [$t("Recipes.ObjectName")]) }}</span>
            </v-tooltip>
            <v-tooltip
              top
              v-if="$permission.permissionAllow('FoodFormula', 'edit')"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  color="primary"
                  class="px-1 py-4 me-1"
                  @click.stop="
                    currentItemMode = 'setting';
                    currentItem = item;
                  "
                  >fa-cog
                </v-icon>
              </template>
              <span>{{ $t("Recipes.recipeSetting") }}</span>
            </v-tooltip>
            <v-tooltip
              top
              v-if="
                $permission.permissionAllow('FoodFormula', 'delete') &&
                item.actions.delete
              "
            >
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  color="danger"
                  class="px-1 me-1"
                  @click.stop="
                    currentItem = item;
                    deleteDialog = true;
                  "
                  >fa-trash-alt
                </v-icon>
              </template>
              <span>
                {{ $t("CRUD.deleteInfo", [$t("Recipes.ObjectName")]) }}
              </span>
            </v-tooltip>
          </template>
          <template v-slot:item.formula_code="{ item }">
            <a
              @click="
                $router.push({
                  path: '/dishes',
                  query: { formula_id: item.id },
                })
              "
            >
              {{ item.formula_code }}
            </a>
          </template>
        </v-data-table>
      </div>

      <v-dialog persistent v-model="editDialog" width="600px">
        <v-card>
          <v-card-title>
            <span class="headline">
              {{ $t("Recipes.costCardInfo") }}
            </span>
            <v-spacer></v-spacer>
            <v-tooltip left>
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" @click="editDialog = false">
                  fa-times
                </v-icon>
              </template>
              <span>
                {{ $t("CRUD.backToList", [$t("Recipes.ObjectName")]) }}
              </span>
            </v-tooltip>
          </v-card-title>
          <v-card-text>
            <v-container>
              <ValidationObserver ref="observer">
                <v-form>
                  <v-row>
                    <v-col cols="12">
                      <ValidationProvider
                        v-slot="{ errors }"
                        :name="$t('Recipes.Fields.recipe_code')"
                        rules="required"
                      >
                        <v-text-field
                          required
                          v-model="currentItem.formula_code"
                          :error-messages="
                            $resparse
                              .getErrorMsgs(serverErrors, 'formula_code')
                              .concat(errors)
                          "
                        >
                          <template v-slot:label>
                            {{ $t("Recipes.Fields.recipe_code") }}
                            <span class="red--text">*</span>
                          </template>
                        </v-text-field>
                      </ValidationProvider>
                    </v-col>
                    <v-col cols="12">
                      <ValidationProvider
                        v-slot="{ errors }"
                        :name="$t('Recipes.Fields.recipe_name')"
                        rules="required"
                      >
                        <v-text-field
                          required
                          v-model="currentItem.formula_name"
                          :error-messages="
                            $resparse
                              .getErrorMsgs(serverErrors, 'formula_name')
                              .concat(errors)
                          "
                        >
                          <template v-slot:label>
                            {{ $t("Recipes.Fields.recipe_name") }}
                            <span class="red--text">*</span>
                          </template>
                        </v-text-field>
                      </ValidationProvider>
                    </v-col>
                  </v-row>
                </v-form>
              </ValidationObserver>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" outlined @click="editDialog = false">
              {{ $t("$vuetify.close") }}
            </v-btn>
            <v-btn color="accent" @click="save()">{{ $t("CRUD.save") }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <DialogDelete
        v-model="deleteDialog"
        :label="$t('Recipes.ObjectName')"
        @confirm="
          del();
          deleteDialog = false;
        "
      />
    </v-sheet>

    <RecipeDetail
      v-show="currentItemMode"
      v-model="currentItem"
      :mode="currentItemMode"
      v-on:mode="currentItemMode = $event"
    />

    <ImportExcelAlert
      v-model="ImportDialog"
      :objName="$t('Recipes.foodBom')"
      downloadTemplateUrl="download/template/foods_bom_template.xlsx"
      @import="
        excelFile = $event;
        importExcel();
      "
    />
    <ImportExcelAlert
      v-model="ImportBatchEditDialog"
      :objName="$t('Recipes.foodBom')"
      :title="$t('Recipes.batchEditBomImport')"
      @import="
        batchEditExcelFile = $event;
        batchEditImports();
      "
      downloadFromAPI
      @download="editTemplateExport()"
    />
    <ImportResult
      v-model="importResultFlg"
      :errData="importResult"
      @close="
        importResultFlg = false;
        getRecipesFromAPI();
      "
    />

    <ImportFoodResult
      v-model="importFoodResultFlg"
      :errData="importFoodResult"
      @close="
        importFoodResultFlg = false;
        getRecipesFromAPI();
      "
    />
    <ImportWithFormulaResult
      v-model="importWithFormulasResultFlg"
      :errData="importWithFormulasResult"
      @close="
        importWithFormulasResultFlg = false;
        getRecipesFromAPI();
      "
    />
    <v-dialog v-model="importBatchEditResultFlg" max-width="800px">
      <v-card class="px-4 pb-4">
        <v-card-title class="mx-3">
          {{ $t("Recipes.errHint") }}
          <v-spacer />
          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                @click="importBatchEditResultFlg = false"
              >
                fa-times
              </v-icon>
            </template>
            <span>{{ $t("$vuetify.close") }}</span>
          </v-tooltip>
        </v-card-title>
        <v-divider></v-divider>
        <v-data-table
          :headers="importBatchEditResultHeaders"
          :items="importBatchEditResult"
          multi-sort
          disable-pagination
          hide-default-footer
          class="pb-2"
        >
          <template v-slot:item.msg="{ item }">
            <span class="red--text">
              {{ item.msg }}
            </span>
          </template>
        </v-data-table>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="secondary"
            outlined
            @click="importBatchEditResultFlg = false"
          >
            {{ $t("$vuetify.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState } from "vuex";
import i18n from "@/i18n";
import PageAllow from "@/components/PageAllow";
import Breadcrumbs from "@/components/Breadcrumbs";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import DialogDelete from "@/components/plugins/DialogDelete";
import RecipeDetail from "./plugins/Detail";
import Recipes from "@/api/recipes";
import ImportExcelAlert from "@/components/plugins/ImportExcelAlert";
import SearcherKeyword from "@/components/plugins/SearcherKeyword";
import ImportResult from "./plugins/ImportResult";
import ImportFoodResult from "./plugins/ImportFoodResult";
import ImportWithFormulaResult from "./plugins/ImportWithFormulaResult";

const defaultOptions = {
  formula_keyword: "",
};
export default {
  components: {
    PageAllow,
    Breadcrumbs,
    ValidationProvider,
    ValidationObserver,
    DialogDelete,
    RecipeDetail,
    ImportExcelAlert,
    SearcherKeyword,
    ImportResult,
    ImportFoodResult,
    ImportWithFormulaResult,
  },

  computed: {
    items: () => [
      {
        text: i18n.t("Nav.home"),
        disabled: false,
        href: "/home",
      },
      {
        text: i18n.t("Nav.dishManagement"),
        disabled: false,
        href: "/dishes",
      },
      {
        text: i18n.t("Nav.recipeManagement"),
        disabled: true,
        href: "/recipes",
      },
    ],

    headers() {
      return [
        {
          text: i18n.t("Recipes.Fields.recipe_code"),
          align: "start",
          class: "text-no-wrap",
          sortable: false,
          value: "formula_code",
        },
        {
          text: i18n.t("Recipes.Fields.recipe_name"),
          align: "start",
          class: "text-no-wrap",
          sortable: false,
          value: "formula_name",
        },
        {
          text: i18n.t("Recipes.Fields.binded_cuisine"),
          align: "center",
          class: "text-no-wrap",
          sortable: false,
          value: "food_count",
        },
        {
          text: i18n.t("TableHeaders.actions"),
          align: "start",
          class: "text-no-wrap",
          sortable: false,
          value: "actions",
        },
      ];
    },
    importBatchEditResultHeaders() {
      return [
        {
          text: i18n.t("Recipes.Fields.formula_code"),
          align: "center",
          sortable: false,
          class: "text-no-wrap",
          value: "formula_code",
        },
        {
          text: i18n.t("Recipes.Fields.formula_name"),
          align: "center",
          sortable: false,
          class: "text-no-wrap",
          value: "formula_name",
        },
        {
          text: i18n.t("Recipes.Fields.material_code"),
          align: "center",
          sortable: false,
          class: "text-no-wrap",
          value: "material_code",
        },
        {
          text: i18n.t("Recipes.Fields.material_name"),
          align: "center",
          sortable: false,
          class: "text-no-wrap",
          value: "material_name",
        },
        {
          text: i18n.t("Recipes.Fields.with_formula_code"),
          align: "center",
          sortable: false,
          class: "text-no-wrap",
          value: "with_formula_code",
        },
        {
          text: i18n.t("Recipes.Fields.with_formula_name"),
          align: "center",
          sortable: false,
          class: "text-no-wrap",
          value: "with_formula_name",
        },
        {
          text: i18n.t("Recipes.Fields.msg"),
          align: "center",
          sortable: false,
          class: "text-no-wrap",
          value: "msg",
        },
      ];
    },

    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["loading"]),
  },

  data: () => ({
    menu: false,
    editDialog: false,
    deleteDialog: false,
    recipes: [],
    formulaKeyword: "",
    options: JSON.parse(JSON.stringify(defaultOptions)),
    currentItemMode: null,
    currentItem: {},
    paging: {},
    serverErrors: [],
    excelFile: null,
    ImportDialog: false,
    importResultFlg: false,
    importResult: [],
    importFoodResultFlg: false,
    importFoodResult: [],
    importWithFormulasResultFlg: false,
    importWithFormulasResult: [],
    batchEditExcelFile: null,
    ImportBatchEditDialog: false,
    importBatchEditResultFlg: false,
    importBatchEditResult: [],
  }),

  mounted() {
    this.getRecipesFromAPI();
  },

  methods: {
    onSearch() {
      this.options = Object.assign({}, this.options, {
        formula_keyword: this.formulaKeyword,
      });
    },
    resetSearch() {
      this.options = JSON.parse(JSON.stringify(defaultOptions));
    },
    getRecipesFromAPI() {
      let options = JSON.parse(JSON.stringify(this.options));
      Recipes.fetch(options).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.recipes = response.data.food_formula;
            this.paging = response.data.paging;
          } else if (response.data.error.code.isNoResultSet()) {
            this.options.page = 1;
          }
        }.bind(this)
      );
    },

    save() {
      this.serverErrors = [];
      this.$refs.observer.validate().then(
        function (result) {
          if (result) {
            this.$store.dispatch("fullscreenLoading");
            Recipes.save(this.currentItem.id, this.currentItem)
              .then(
                function (response) {
                  if (response.data.status.isOK()) {
                    this.$emit("save", this.currentItem);
                    this.getRecipesFromAPI();
                    this.editDialog = false;
                    this.$store.dispatch("fullscreenLoadingStop");
                  } else if (
                    response.data.error &&
                    response.data.error.data &&
                    "ERR333" == response.data.error.code
                  ) {
                    this.serverErrors = response.data.error.data;
                    this.$store.dispatch("fullscreenLoadingStop");
                  }
                }.bind(this)
              )
              .catch(
                function () {
                  this.editDialog = false;
                  this.$store.dispatch("fullscreenLoadingStop");
                }.bind(this)
              );
          }
        }.bind(this)
      );
    },
    del() {
      Recipes.del(this.currentItem.id).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.getRecipesFromAPI();
          }
        }.bind(this)
      );
    },
    importExcel() {
      this.$store.dispatch("fullscreenLoading");
      Recipes.imports(this.excelFile)
        .then(
          function (response) {
            if (response.data.status.isOK()) {
              if (
                response.data.error_meterials &&
                Object.keys(response.data.error_meterials).length
              ) {
                this.ImportDialog = false;
                this.importResultFlg = true;
                this.importResult = Object.values(
                  response.data.error_meterials
                );
              } else if (
                response.data.error_foods &&
                Object.keys(response.data.error_foods).length
              ) {
                this.ImportDialog = false;
                this.importFoodResultFlg = true;
                this.importFoodResult = Object.values(
                  response.data.error_foods
                );
              } else if (
                response.data.error_with_formulas &&
                Object.keys(response.data.error_with_formulas).length
              ) {
                this.ImportDialog = false;
                this.importWithFormulasResultFlg = true;
                this.importWithFormulasResult = Object.values(
                  response.data.error_with_formulas
                );
              } else {
                this.$snackbar.info(
                  i18n.t("Import.success", [i18n.t("Recipes.foodBom")])
                );
                this.ImportDialog = false;
                this.getRecipesFromAPI();
              }
            }
            this.$store.dispatch("fullscreenLoadingStop");
          }.bind(this)
        )
        .catch(
          function () {
            this.$store.dispatch("fullscreenLoadingStop");
          }.bind(this)
        );
    },
    batchEditImports() {
      this.$store.dispatch("fullscreenLoading");
      Recipes.batchEditImports(this.batchEditExcelFile)
        .then(
          function (response) {
            if (response.data.status.isOK()) {
              if (
                response.data.error_formula &&
                response.data.error_formula.length
              ) {
                this.ImportBatchEditDialog = false;
                this.importBatchEditResultFlg = true;
                this.importBatchEditResult = response.data.error_formula;
              } else {
                this.$snackbar.info(
                  i18n.t("Import.success", [i18n.t("Recipes.foodBom")])
                );
                this.ImportBatchEditDialog = false;
                this.getRecipesFromAPI();
              }
            }
            this.$store.dispatch("fullscreenLoadingStop");
          }.bind(this)
        )
        .catch(
          function () {
            this.$store.dispatch("fullscreenLoadingStop");
          }.bind(this)
        );
    },
    editTemplateExport() {
      Recipes.editTemplateExport(this.options);
    },
  },

  watch: {
    $route() {
      if (!this.$route.query.id) {
        this.currentItemMode = null;
        this.currentItem = null;
      }
    },
    currentItemMode: {
      handler() {
        if (!this.currentItemMode) {
          this.currentItem = {};
        }
      },
    },
    currentItem: {
      handler() {
        if (this.currentItem) {
          this.$router.replace("/recipes?id=" + this.currentItem.id);
          this.$cacheScrollLocation();
        } else {
          this.$scrollToCachedLocation();
          this.$router.replace("/recipes");
        }
      },
    },
    editDialog: {
      handler() {
        if (!this.editDialog) {
          this.$refs.observer.reset();
          this.serverErrors = [];
          this.$store.dispatch("fullscreenLoadingStop");
        }
      },
    },
    options: {
      // immediate: true, //为避免覆盖session里保存的options，不能启用immediate
      deep: true,
      handler() {
        this.getRecipesFromAPI();
      },
    },
  },
};
</script>